import type {FC, ReactEventHandler} from 'react';
import React, { useRef, useState} from 'react'
import type {PercentCrop, ReactCropProps} from 'react-image-crop';
import ReactCrop from 'react-image-crop'
import useEvent from '../hooks/useEvent'
import ModalDialog from './ModalDialog'
import {FlexButtonBar} from './buttons'
import {CancelButton, SaveButton} from './SaveSubmitButton'
import icons from '../../markons/markons'
import 'react-image-crop/dist/ReactCrop.css'
import {captureSentryError} from '../sentry'
import {notifyError} from '../notify'
import css from './ImageCropPopup.less'

export interface ImageCropPopupProps {
    src: string
    onSave: (crop: RatioCrop, image: HTMLImageElement) => void
    onCancel: VoidFn
    initialAspectRatio: number,
    forceAspectRatio?: boolean
}

type CropCallbacks = PickByType<ReactCropProps, AnyFn>

export interface RatioCrop {
    // unit?: '⅌'
    x: number
    y: number
    width: number
    height: number
}

const DEFAULT_CROP = Object.freeze<RatioCrop>({
    // "unit": "⅌",
    "x": 0,
    "y": 0,
    "width": 0,
    "height": 0
})
export const ImageCropPopup: FC<ImageCropPopupProps> = ({src, onSave, onCancel, initialAspectRatio, forceAspectRatio}) => {
    const [crop, setCrop] = useState<PercentCrop>()
    const imgRef = useRef<HTMLImageElement>(null)

    const onCropChange = useEvent<CropCallbacks['onChange']>((pixelCrop, percentCrop) => {
        setCrop(percentCrop)
    })

    const onImageLoad = useEvent<ReactEventHandler<HTMLImageElement>>(ev => {
        const {naturalWidth, naturalHeight} = ev.currentTarget

        if(!naturalWidth || !naturalHeight) {
            captureSentryError("Could not read image size",null,{naturalWidth,naturalHeight},'cbc35f3a-01d7-461b-b8bf-6c0a5e554132')
            notifyError("Could not read image size")
            return
        }

        let width = naturalWidth * 0.975
        let height = naturalHeight * 0.975

        if(width >= height * initialAspectRatio) {
            width = height * initialAspectRatio
        } else {
            height = width / initialAspectRatio
        }

       const widthPercent = width / naturalWidth * 100
       const heightPercent = height / naturalHeight * 100

        setCrop({
            unit: '%',
            width: width / naturalWidth * 100,
            height: height / naturalHeight * 100,
            x: (100 - widthPercent) / 2,
            y: (100 - heightPercent) / 2,
        })
    })

    const saveHandler = useEvent(() => {
        if(!crop || !imgRef.current) {
            captureSentryError("Could not get image or crop size",null,null,'8d567431-a45e-4bb2-aa20-cfe67a01f2c4')
            notifyError("Could not get image or crop size")
            return
        }
        onSave({
            x: crop.x / 100,
            y: crop.y / 100,
            width: crop.width / 100,
            height: crop.height / 100,
        }, imgRef.current)
    })

    return (
        <ModalDialog title="Crop Image">
            <ReactCrop crop={crop} aspect={forceAspectRatio && initialAspectRatio ? initialAspectRatio : undefined} onChange={onCropChange} onComplete={onCropChange} keepSelection>
                <img ref={imgRef} className={css.cropImage} alt="Image to crop" src={src} style={{maxWidth: '100%'}} onLoad={onImageLoad}/>
            </ReactCrop>

            <FlexButtonBar>
                <SaveButton icon={icons.faCloudUpload} processing={false} onClick={saveHandler}>Upload</SaveButton>
                <CancelButton onClick={onCancel}>Cancel</CancelButton>
            </FlexButtonBar>
        </ModalDialog>
    )
}
